import AlertsContext from "providers/AlertProvider";
import useRouter from "common/hooks/use-router";
import useItem from "hooks/useItem";
import useList from "hooks/useList";
import { useCallback, useContext, useEffect } from "react";
import type { Plot, Plotstep } from "types";
import PageLoading from "common/components/PageLoading/PageLoading";
import Icon from "components/Icon/Icon";
import StickyWrapper from "common/components/StickyWrapper/StickyWrapper";
import StarRating from "components/StarRating/StarRating";

function formatDate(dateStr: string | undefined) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

type InfoRowProps = { label: string; value: React.ReactNode };

function InfoRow({ label, value }: InfoRowProps) {
  return (
    <div className="flex flex-col gap-0.5 py-3 border-b border-slate-100 last:border-0">
      <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">
        {label}
      </span>
      <span className="text-sm font-semibold text-slate-700">{value ?? "—"}</span>
    </div>
  );
}

function PlotDetails() {
  const router = useRouter();
  const { setAlert } = useContext(AlertsContext);
  const id = router.query.id as string;

  const { item, error, isFetching } = useItem<Plot>("plots", id, {
    populate: { plotsteps: { sort: ["createdAt:asc"], populate: ["step"] }, ricePaddy: true },
  });

  const { items: reviews } = useList<any>("reviews", {
    defaultFilters: { "plot_id$eq": id },
    populate: ["plotstep_id"],
    cachePrefix: `reviews-plot-${id}`,
  });

  const handleBack = useCallback(() => {
    if (window.history.length > 1) {
      router.navigate(-1);
      return;
    }
    router.navigate("/plots");
  }, [router]);

  useEffect(() => {
    if (error) {
      setAlert(error, "danger");
    }
  }, [error, setAlert]);

  const getReviewForPlotStep = useCallback(
    (plotstepId: number) =>
      reviews?.find((r: any) => r.plotstep_id?.id === plotstepId),
    [reviews]
  );

  if (isFetching && !item) {
    return <PageLoading />;
  }

  const plot = (item as Plot) || {};
  const plotsteps: Plotstep[] = plot.plotsteps ?? [];
  const currentPlotstep = plotsteps.length > 0 ? plotsteps[plotsteps.length - 1] : null;

  return (
    <div className="mx-auto max-w-full text-slate-500 text-base">
      {/* Header */}
      <StickyWrapper isHeader disabledOnMobile>
        <div className="border-b border-slate-200 bg-white px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={handleBack}
              className="flex justify-center items-center w-8 h-8 transition-transform hover:-translate-x-1"
            >
              <Icon name="ChevronLeftIcon" className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-primary">
                {plot.name ?? `Parcelle #${id}`}
              </h1>
              <p className="text-sm text-slate-500">
                ID unique :{" "}
                <span className="font-mono">{plot.uniqueID ?? "—"}</span>
              </p>
            </div>
          </div>
        </div>
      </StickyWrapper>

      {/* Body */}
      <div className="flex flex-col lg:flex-row gap-6 p-6">
        {/* Colonne gauche — Infos principales */}
        <div className="flex-1 flex flex-col gap-6">
          {/* Infos générales */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-base font-bold text-slate-700 mb-4 flex items-center gap-2">
              <Icon name="MapPinIcon" className="w-5 h-5 text-primary" />
              Informations de la parcelle
            </h2>
            <div>
              <InfoRow label="Nom" value={plot.name} />
              <InfoRow label="Variété" value={plot.variety} />
              <InfoRow label="Rizière" value={plot.ricePaddy?.name} />
              <InfoRow
                label="Mobile ID"
                value={
                  <span className="font-mono text-slate-600">
                    {plot.mobileID}
                  </span>
                }
              />
              <InfoRow
                label="ID unique"
                value={
                  <span className="font-mono text-slate-600">
                    {plot.uniqueID}
                  </span>
                }
              />
            </div>
          </div>

          {/* Stade actuel */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-base font-bold text-slate-700 mb-4 flex items-center gap-2">
              <Icon name="ArrowLongUpIcon" className="w-5 h-5 text-primary" />
              Etape actuelle
            </h2>
            <div className="flex items-center gap-4">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-primary text-white text-sm font-bold shrink-0">
                {plot.step}
              </span>
              <div>
                {currentPlotstep?.step?.readLabel && (
                  <p className="text-sm font-semibold text-slate-700">
                    {currentPlotstep.step.readLabel}
                  </p>
                )}
                {currentPlotstep?.step?.needDate !== false && (
                  <p className="text-sm text-slate-400">
                    Depuis le {formatDate(plot.stepDate)}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Colonne droite — Historique des étapes */}
        <div className="w-full lg:w-80 shrink-0">
          <div className="bg-white rounded-xl border border-slate-200 p-6 sticky top-24">
            <h2 className="text-base font-bold text-slate-700 mb-5 flex items-center gap-2">
              <Icon name="ClockIcon" className="w-5 h-5 text-primary" />
              Historique des étapes
              <span className="ml-auto text-xs font-normal text-slate-400 bg-slate-100 rounded-full px-2 py-0.5">
                {plotsteps.length}
              </span>
            </h2>

            {plotsteps.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">
                Aucun historique disponible
              </p>
            ) : (
              <ol className="relative border-l border-slate-200 ml-3">
                {plotsteps.map((ps, index) => (
                  <li key={ps.id} className="mb-6 ml-5 last:mb-0">
                    <span
                      className={`absolute -left-3 flex items-center justify-center w-6 h-6 rounded-full ring-4 ring-white text-xs font-bold
                        ${index === plotsteps.length - 1
                          ? "bg-primary text-white"
                          : "bg-slate-100 text-slate-500"
                        }`}
                    >
                      {ps.step?.progress ?? "?"}
                    </span>
                    <p className="text-sm font-semibold text-slate-700 leading-tight">
                      {ps.step?.readLabel ?? "—"}
                    </p>
                    {ps.step?.needDate !== false && (
                      <time className="text-xs text-slate-400">
                        {formatDate(ps.stepDate ?? undefined)}
                      </time>
                    )}
                    {ps.step?.enableReview && (() => {
                      const review = getReviewForPlotStep(ps.id);
                      return (
                        <div className="mt-1 flex flex-col gap-0.5">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-400 w-14">Météo</span>
                            <StarRating value={review?.gradeWeather ?? 0} readonly />
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-400 w-14">Conseils</span>
                            <StarRating value={review?.gradeAdvice ?? 0} readonly />
                          </div>
                        </div>
                      );
                    })()}
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PlotDetails;
